import React from 'react';

import type { IntlShape } from 'react-intl-next';

import { convertToInlineCss } from '@atlaskit/editor-common/lazy-node-view';
import { type PortalProviderAPI } from '@atlaskit/editor-common/portal';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { type Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import {
	PluginKey,
	type EditorState,
	type ReadonlyTransaction,
} from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';

import type { AiSuggestionsPlugin, Suggestion } from '../aiSuggestionsPluginType';
import { SuggestionIconButton } from '../ui/components/SuggestionIconButton';
import { fetchSuggestions } from '../ui/utils/fetch';
export type AiSuggestionsPluginState = {
	decorations: DecorationSet;
	selectedSuggestion: string | undefined;
	suggestions: Suggestion[];
};

export const aiSuggestionsPluginKey = new PluginKey<AiSuggestionsPluginState>(
	'aiSuggestionsPlugin',
);

export type AiSuggestionsMeta =
	| { action: 'SET_SUGGESTIONS'; suggestions: Suggestion[] }
	| { action: 'CLEAR_SUGGESTIONS' }
	| { action: 'SELECT_SUGGESTION'; id: string | undefined }
	| { action: 'REMOVE_SUGGESTION'; id: string };

function calculateSuggestionsDecorations({
	doc,
	suggestions,
	api,
	intl,
	selectedSuggestion,
	nodeViewPortalProviderAPI,
}: {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
	doc: PMNode;
	intl: IntlShape | undefined;
	nodeViewPortalProviderAPI: PortalProviderAPI;
	selectedSuggestion: string | undefined;
	suggestions: Suggestion[];
}): DecorationSet {
	if (suggestions.length === 0) {
		return DecorationSet.empty;
	}

	const decorations: Decoration[] = [];

	doc.descendants((node, pos) => {
		const nodeLocalId = node.attrs?.localId;
		const suggestion = suggestions.find((s) =>
			s.toolCalls.some((t) => t.arguments?.localId === nodeLocalId),
		);

		if (suggestion) {
			const element = document.createElement('span');
			element.setAttribute(
				'style',
				convertToInlineCss({
					position: 'relative',
					width: '100%',
					display: 'block',
				}),
			);

			const key = crypto.randomUUID();

			decorations.push(
				Decoration.widget(
					pos,
					() => {
						nodeViewPortalProviderAPI.render(
							() => (
								<SuggestionIconButton
									suggestion={suggestion}
									selectedSuggestion={selectedSuggestion}
									api={api}
									intl={intl}
								/>
							),
							element,
							key,
						);
						return element;
					},
					{
						side: 1,
						key,
						destroy: () => {
							// Clean up properly
							// nodeViewPortalProviderAPI.remove(key);
						},
					},
				),
			);
		}
	});

	if (!decorations.length) {
		return DecorationSet.empty;
	}

	return DecorationSet.create(doc, decorations);
}

const initState: AiSuggestionsPluginState = {
	decorations: DecorationSet.empty,
	suggestions: [],
	selectedSuggestion: undefined,
};

const preloadSuggestions = async ({
	api,
	signal,
	contentAri,
}: {
	api: ExtractInjectionAPI<AiSuggestionsPlugin>;
	contentAri: string;
	signal: AbortSignal;
}) => {
	const suggestions = await fetchSuggestions({
		contentAri,
		generate: false,
		signal,
	});
	api?.core?.actions?.execute(({ tr }) => {
		tr.setMeta(aiSuggestionsPluginKey, {
			action: 'SET_SUGGESTIONS',
			suggestions,
		});
		return tr;
	});
};

export function createPlugin({
	api,
	intl,
	contentAri,
	nodeViewPortalProviderAPI,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	contentAri?: string;
	intl?: IntlShape;
	nodeViewPortalProviderAPI: PortalProviderAPI;
}): SafePlugin<AiSuggestionsPluginState> {
	return new SafePlugin<AiSuggestionsPluginState>({
		key: aiSuggestionsPluginKey,
		state: {
			init(_config, _state: EditorState): AiSuggestionsPluginState {
				return initState;
			},
			apply(
				tr: ReadonlyTransaction,
				pluginState: AiSuggestionsPluginState,
				_oldState: EditorState,
				newState: EditorState,
			): AiSuggestionsPluginState {
				const meta = tr.getMeta(aiSuggestionsPluginKey) as AiSuggestionsMeta | undefined;
				let nextState = pluginState;

				if (meta) {
					switch (meta.action) {
						case 'SET_SUGGESTIONS':
							nextState = {
								...pluginState,
								suggestions: meta.suggestions,
								decorations: calculateSuggestionsDecorations({
									doc: newState.doc,
									suggestions: meta.suggestions,
									api,
									intl,
									selectedSuggestion: pluginState.selectedSuggestion,
									nodeViewPortalProviderAPI,
								}),
							};
							break;
						case 'CLEAR_SUGGESTIONS':
							nextState = {
								...pluginState,
								suggestions: [],
								decorations: DecorationSet.empty,
							};
							break;
						case 'SELECT_SUGGESTION':
							nextState = {
								...pluginState,
								selectedSuggestion: meta.id,
								decorations: calculateSuggestionsDecorations({
									doc: newState.doc,
									suggestions: pluginState.suggestions,
									api,
									intl,
									selectedSuggestion: meta.id,
									nodeViewPortalProviderAPI,
								}),
							};
							break;
						case 'REMOVE_SUGGESTION': {
							const suggestions = pluginState.suggestions.filter((s) => s.id !== meta.id);
							const selectedSuggestion =
								pluginState.selectedSuggestion === meta.id
									? undefined
									: pluginState.selectedSuggestion;
							nextState = {
								...pluginState,
								suggestions,
								selectedSuggestion,
								decorations: calculateSuggestionsDecorations({
									doc: newState.doc,
									suggestions: suggestions,
									api,
									intl,
									selectedSuggestion: selectedSuggestion,
									nodeViewPortalProviderAPI,
								}),
							};
							break;
						}
						default:
							break;
					}
				} else {
					nextState = {
						...pluginState,
						decorations: pluginState.decorations.map(tr.mapping, tr.doc),
					};
				}

				return nextState;
			},
		},
		props: {
			decorations(state: EditorState) {
				return aiSuggestionsPluginKey.getState(state)?.decorations ?? DecorationSet.empty;
			},
		},
		view: () => {
			const abortController = new AbortController();
			if (contentAri && api) {
				preloadSuggestions({ contentAri, api, signal: abortController.signal });
			}
			return {
				destroy: () => {
					abortController.abort();
				},
			};
		},
	});
}
